// Deep-link helpers for the Home "Comece com o pé direito" setup checklist.
//
// When the user clicks a checklist item, Home stores:
//   - a per-module *view hint* (e.g. kinng.route.whatsapp.view = "conexao")
//     so the target module opens straight on the right screen instead of its hub;
//   - the flag kinng.route.returnToHome = "1" so that, once the action is saved
//     (or the user backs out), the module sends them straight back to Home.
//
// Every access is wrapped in try/catch because sessionStorage can throw
// (private mode, disabled storage) and must never break navigation.

const RETURN_HOME_KEY = "kinng.route.returnToHome";

/** Read a one-shot routing hint and immediately clear it. */
export const consumeRouteHint = (key) => {
    try {
        const value = sessionStorage.getItem(key);
        if (value) sessionStorage.removeItem(key);
        return value;
    } catch (_) {
        return null;
    }
};

/**
 * Read a routing hint WITHOUT clearing it — safe to call from a render / a
 * useState initializer (which React double-invokes under StrictMode). Pair it
 * with clearRouteHint() in a mount effect for the one-shot semantics.
 */
export const peekRouteHint = (key) => {
    try {
        return sessionStorage.getItem(key);
    } catch (_) {
        return null;
    }
};

/** Store a one-shot routing hint for the module about to be opened. */
export const setRouteHint = (key, value) => {
    try {
        sessionStorage.setItem(key, value);
    } catch (_) {
        /* ignore */
    }
};

/**
 * Open a system alert's target module. When the alert names a `targetView`,
 * the module skips its hub and lands straight on that screen (alerts are
 * "resolve this now" shortcuts — no extra click).
 */
export const openAlertTarget = (alert, setActiveModule) => {
    if (!alert.target) return;
    if (alert.targetView) setRouteHint(`kinng.route.${alert.target}.view`, alert.targetView);
    setActiveModule(alert.target);
};

/** Remove a routing hint (idempotent — safe to call more than once). */
export const clearRouteHint = (key) => {
    try {
        sessionStorage.removeItem(key);
    } catch (_) {
        /* ignore */
    }
};

/** True when Home asked the target module to bounce back once it's done. */
export const shouldReturnHome = () => {
    try {
        return sessionStorage.getItem(RETURN_HOME_KEY) === "1";
    } catch (_) {
        return false;
    }
};

/** Ask the target module to bounce back to Home once the action is done. */
export const setSetupReturnHome = () => {
    try {
        sessionStorage.setItem(RETURN_HOME_KEY, "1");
    } catch (_) {
        /* ignore */
    }
};

/** Clear the return-to-Home flag (call right before navigating home). */
export const clearReturnHome = () => {
    try {
        sessionStorage.removeItem(RETURN_HOME_KEY);
    } catch (_) {
        /* ignore */
    }
};

/**
 * If Home requested a return trip, clear the flag and run `goHome`, returning
 * true. Otherwise return false so the caller can fall back to its own hub.
 */
export const returnHomeIfRequested = (goHome) => {
    if (!shouldReturnHome()) return false;
    clearReturnHome();
    goHome();
    return true;
};
